import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { LoadingState } from '@/components/ui/states';
import { StorefrontLayout } from '@/layouts/storefront-layout';
import { HomePage } from '@/pages/home-page';
import { NotFoundPage } from '@/pages/not-found-page';

/**
 * ROTA AYRIMI
 *
 * Storefront ve yönetim paneli ayrı dünyalardır: admin bileşenleri ve ona
 * özel ağır kütüphaneler storefront bundle'ına GİRMEZ.
 *
 * Ana sayfa eager yüklenir (ilk boyama), diğer storefront sayfaları ve
 * yönetim panelinin tamamı lazy'dir.
 */

const CatalogPage = lazy(() =>
  import('@/pages/catalog-page').then((module) => ({ default: module.CatalogPage })),
);

const ProductDetailPage = lazy(() =>
  import('@/pages/product-detail-page').then((module) => ({
    default: module.ProductDetailPage,
  })),
);

const CartPage = lazy(() =>
  import('@/pages/cart-page').then((module) => ({ default: module.CartPage })),
);

const CheckoutPage = lazy(() =>
  import('@/pages/checkout-page').then((module) => ({
    default: module.CheckoutPage,
  })),
);

const OrderSuccessPage = lazy(() =>
  import('@/pages/order-success-page').then((module) => ({
    default: module.OrderSuccessPage,
  })),
);

const SearchPage = lazy(() =>
  import('@/pages/search-page').then((module) => ({ default: module.SearchPage })),
);

const FavoritesPage = lazy(() =>
  import('@/pages/favorites-page').then((module) => ({
    default: module.FavoritesPage,
  })),
);

const OrderTrackingPage = lazy(() =>
  import('@/pages/order-tracking-page').then((module) => ({
    default: module.OrderTrackingPage,
  })),
);

const DesignSystemPage = lazy(() =>
  import('@/pages/design-system-page').then((module) => ({
    default: module.DesignSystemPage,
  })),
);

const AdminLayout = lazy(() =>
  import('@/layouts/admin-layout').then((module) => ({ default: module.AdminLayout })),
);

const AdminLoginPage = lazy(() =>
  import('@/features/admin/pages/admin-login-page').then((module) => ({
    default: module.AdminLoginPage,
  })),
);

const AdminTodayPage = lazy(() =>
  import('@/features/admin/pages/admin-today-page').then((module) => ({
    default: module.AdminTodayPage,
  })),
);

const AdminOrdersPage = lazy(() =>
  import('@/features/admin/pages/admin-orders-page').then((module) => ({
    default: module.AdminOrdersPage,
  })),
);

const AdminOrderDetailPage = lazy(() =>
  import('@/features/admin/pages/admin-order-detail-page').then((module) => ({
    default: module.AdminOrderDetailPage,
  })),
);

const AdminProductsPage = lazy(() =>
  import('@/features/admin/pages/admin-products-page').then((module) => ({
    default: module.AdminProductsPage,
  })),
);

const AdminProductFormPage = lazy(() =>
  import('@/features/admin/pages/admin-product-form-page').then((module) => ({
    default: module.AdminProductFormPage,
  })),
);

const AdminDeliveryPage = lazy(() =>
  import('@/features/admin/pages/admin-delivery-page').then((module) => ({
    default: module.AdminDeliveryPage,
  })),
);

// Faz 8 — kurumsal ve yerel SEO sayfaları.
const AboutPage = lazy(() =>
  import('@/features/corporate/about-page').then((m) => ({ default: m.AboutPage })),
);

const ContactPage = lazy(() =>
  import('@/features/corporate/contact-page').then((m) => ({ default: m.ContactPage })),
);

const FaqPage = lazy(() =>
  import('@/features/corporate/faq-page').then((m) => ({ default: m.FaqPage })),
);

const DeliveryInfoPage = lazy(() =>
  import('@/features/corporate/delivery-info-page').then((m) => ({
    default: m.DeliveryInfoPage,
  })),
);

/**
 * Tek parça adresler (yerel SEO sayfaları ve hukuki metinler) tek bir
 * çözümleyiciden geçer: liste veri tabanından geldiği için rota tablosuna
 * sabit yol yazılamaz.
 */
const PublicSlugRoute = lazy(() =>
  import('@/features/corporate/corporate-routes').then((m) => ({
    default: m.PublicSlugRoute,
  })),
);

// Faz 7 — müşteri hesabı ekranları.
const LoginPage = lazy(() =>
  import('@/features/account/pages/login-page').then((module) => ({
    default: module.LoginPage,
  })),
);

const RegisterPage = lazy(() =>
  import('@/features/account/pages/register-page').then((module) => ({
    default: module.RegisterPage,
  })),
);

const AccountPage = lazy(() =>
  import('@/features/account/pages/account-page').then((module) => ({
    default: module.AccountPage,
  })),
);

const ProfilePage = lazy(() =>
  import('@/features/account/pages/profile-page').then((module) => ({
    default: module.ProfilePage,
  })),
);

const OrdersPage = lazy(() =>
  import('@/features/account/pages/orders-page').then((module) => ({
    default: module.OrdersPage,
  })),
);

const OrderDetailPage = lazy(() =>
  import('@/features/account/pages/order-detail-page').then((module) => ({
    default: module.OrderDetailPage,
  })),
);

const AddressesPage = lazy(() =>
  import('@/features/account/pages/addresses-page').then((module) => ({
    default: module.AddressesPage,
  })),
);

const RequireAuth = lazy(() =>
  import('@/features/account/components/require-auth').then((module) => ({
    default: module.RequireAuth,
  })),
);

// Faz 6 — içerik ve ticaret yönetimi ekranları.
const AdminContentPage = lazy(() =>
  import('@/features/admin/pages/admin-content-page').then((module) => ({
    default: module.AdminContentPage,
  })),
);

const AdminHomepagePage = lazy(() =>
  import('@/features/admin/pages/admin-homepage-page').then((module) => ({
    default: module.AdminHomepagePage,
  })),
);

const AdminCategoriesPage = lazy(() =>
  import('@/features/admin/pages/admin-categories-page').then((module) => ({
    default: module.AdminCategoriesPage,
  })),
);

const AdminCollectionsPage = lazy(() =>
  import('@/features/admin/pages/admin-collections-page').then((module) => ({
    default: module.AdminCollectionsPage,
  })),
);

const AdminCampaignsPage = lazy(() =>
  import('@/features/admin/pages/admin-campaigns-page').then((module) => ({
    default: module.AdminCampaignsPage,
  })),
);

const AdminAddonsPage = lazy(() =>
  import('@/features/admin/pages/admin-addons-page').then((module) => ({
    default: module.AdminAddonsPage,
  })),
);

const AdminCouponsPage = lazy(() =>
  import('@/features/admin/pages/admin-coupons-page').then((module) => ({
    default: module.AdminCouponsPage,
  })),
);

const AdminOccasionsPage = lazy(() =>
  import('@/features/admin/pages/admin-occasions-page').then((module) => ({
    default: module.AdminOccasionsPage,
  })),
);

const AdminReviewsPage = lazy(() =>
  import('@/features/admin/pages/admin-reviews-page').then((module) => ({
    default: module.AdminReviewsPage,
  })),
);

const AdminInstagramPage = lazy(() =>
  import('@/features/admin/pages/admin-instagram-page').then((module) => ({
    default: module.AdminInstagramPage,
  })),
);

const AdminBankAccountsPage = lazy(() =>
  import('@/features/admin/pages/admin-bank-accounts-page').then((module) => ({
    default: module.AdminBankAccountsPage,
  })),
);

const AdminLegalPage = lazy(() =>
  import('@/features/admin/pages/admin-legal-page').then((module) => ({
    default: module.AdminLegalPage,
  })),
);

const AdminSettingsPage = lazy(() =>
  import('@/features/admin/pages/admin-settings-page').then((module) => ({
    default: module.AdminSettingsPage,
  })),
);

const RequireAdmin = lazy(() =>
  import('@/features/admin/components/require-admin').then((module) => ({
    default: module.RequireAdmin,
  })),
);

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<LoadingState />}>{children}</Suspense>;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <StorefrontLayout />,
    errorElement: <NotFoundPage />,
    children: [
      { index: true, element: <HomePage /> },
      {
        path: 'kategori/:slug',
        element: (
          <Lazy>
            <CatalogPage kind="category" />
          </Lazy>
        ),
      },
      {
        path: 'koleksiyon/:slug',
        element: (
          <Lazy>
            <CatalogPage kind="collection" />
          </Lazy>
        ),
      },
      {
        path: 'urun/:slug',
        element: (
          <Lazy>
            <ProductDetailPage />
          </Lazy>
        ),
      },
      {
        path: 'sepet',
        element: (
          <Lazy>
            <CartPage />
          </Lazy>
        ),
      },
      {
        path: 'odeme',
        element: (
          <Lazy>
            <CheckoutPage />
          </Lazy>
        ),
      },
      {
        path: 'siparis/:orderNo/tesekkurler',
        element: (
          <Lazy>
            <OrderSuccessPage />
          </Lazy>
        ),
      },
      {
        path: 'arama',
        element: (
          <Lazy>
            <SearchPage />
          </Lazy>
        ),
      },
      {
        path: 'favorilerim',
        element: (
          <Lazy>
            <FavoritesPage />
          </Lazy>
        ),
      },
      {
        path: 'siparis-takip',
        element: (
          <Lazy>
            <OrderTrackingPage />
          </Lazy>
        ),
      },
      {
        path: 'tasarim-sistemi',
        element: (
          <Lazy>
            <DesignSystemPage />
          </Lazy>
        ),
      },

      // --- Hesap ---------------------------------------------------------
      // Giriş/kayıt korumasızdır; diğerleri RequireAuth arkasındadır.
      {
        path: 'giris',
        element: (
          <Lazy>
            <LoginPage />
          </Lazy>
        ),
      },
      {
        path: 'kayit',
        element: (
          <Lazy>
            <RegisterPage />
          </Lazy>
        ),
      },
      {
        path: 'hesabim',
        element: (
          <Lazy>
            <RequireAuth>
              <AccountPage />
            </RequireAuth>
          </Lazy>
        ),
      },
      {
        path: 'hesabim/profil',
        element: (
          <Lazy>
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          </Lazy>
        ),
      },
      {
        path: 'siparislerim',
        element: (
          <Lazy>
            <RequireAuth>
              <OrdersPage />
            </RequireAuth>
          </Lazy>
        ),
      },
      {
        path: 'siparislerim/:orderNo',
        element: (
          <Lazy>
            <RequireAuth>
              <OrderDetailPage />
            </RequireAuth>
          </Lazy>
        ),
      },
      {
        path: 'adreslerim',
        element: (
          <Lazy>
            <RequireAuth>
              <AddressesPage />
            </RequireAuth>
          </Lazy>
        ),
      },

// --- Kurumsal -------------------------------------------------------
            {
              path: 'hakkimizda',
              element: (
                <Lazy>
                  <AboutPage />
                </Lazy>
              ),
            },
            {
              path: 'iletisim',
              element: (
                <Lazy>
                  <ContactPage />
                </Lazy>
              ),
            },
            {
              path: 'sikca-sorulan-sorular',
              element: (
                <Lazy>
                  <FaqPage />
                </Lazy>
              ),
            },
            {
              path: 'teslimat-bilgileri',
              element: (
                <Lazy>
                  <DeliveryInfoPage />
                </Lazy>
              ),
            },
      
      /*
       * Yerel arama sayfaları (/isparta-cicekci, /yalvac-cicekci, ...) ve
       * hukuki metinler (/kvkk) aynı biçimdedir. Hangi yerel sayfaların var
       * olduğu teslimat bölgelerine bağlıdır ve panelden değişir; bu yüzden
       * rota tablosunda sabit liste tutulmaz.
       */
      {
        path: ':slug',
        element: (
          <Lazy>
            <PublicSlugRoute />
          </Lazy>
        ),
      },
      
            { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: '/yonetim/giris',
    element: (
      <Lazy>
        <AdminLoginPage />
      </Lazy>
    ),
  },
  {
    path: '/yonetim',
    element: (
      <Lazy>
        <RequireAdmin>
          <AdminLayout />
        </RequireAdmin>
      </Lazy>
    ),
    children: [
      {
        index: true,
        element: (
          <Lazy>
            <AdminTodayPage />
          </Lazy>
        ),
      },
      {
        path: 'siparisler',
        element: (
          <Lazy>
            <AdminOrdersPage />
          </Lazy>
        ),
      },
      {
        path: 'siparisler/:orderNo',
        element: (
          <Lazy>
            <AdminOrderDetailPage />
          </Lazy>
        ),
      },
      {
        path: 'urunler',
        element: (
          <Lazy>
            <AdminProductsPage />
          </Lazy>
        ),
      },
      {
        path: 'urunler/:id',
        element: (
          <Lazy>
            <AdminProductFormPage />
          </Lazy>
        ),
      },
      {
        path: 'teslimat',
        element: (
          <Lazy>
            <AdminDeliveryPage />
          </Lazy>
        ),
      },

      // İçerik ve ticaret: günlük işleyişten ayrı, tek kapı arkasında.
      {
        path: 'icerik',
        element: (
          <Lazy>
            <AdminContentPage />
          </Lazy>
        ),
      },
      {
        path: 'icerik/ana-sayfa',
        element: (
          <Lazy>
            <AdminHomepagePage />
          </Lazy>
        ),
      },
      {
        path: 'icerik/kategoriler',
        element: (
          <Lazy>
            <AdminCategoriesPage />
          </Lazy>
        ),
      },
      {
        path: 'icerik/koleksiyonlar',
        element: (
          <Lazy>
            <AdminCollectionsPage />
          </Lazy>
        ),
      },
      {
        path: 'icerik/ek-urunler',
        element: (
          <Lazy>
            <AdminAddonsPage />
          </Lazy>
        ),
      },
      {
        path: 'icerik/kampanyalar',
        element: (
          <Lazy>
            <AdminCampaignsPage />
          </Lazy>
        ),
      },
      {
        path: 'icerik/kuponlar',
        element: (
          <Lazy>
            <AdminCouponsPage />
          </Lazy>
        ),
      },
      {
        path: 'icerik/ozel-gunler',
        element: (
          <Lazy>
            <AdminOccasionsPage />
          </Lazy>
        ),
      },
      {
        path: 'icerik/yorumlar',
        element: (
          <Lazy>
            <AdminReviewsPage />
          </Lazy>
        ),
      },
      {
        path: 'icerik/instagram',
        element: (
          <Lazy>
            <AdminInstagramPage />
          </Lazy>
        ),
      },
      {
        path: 'icerik/banka-hesaplari',
        element: (
          <Lazy>
            <AdminBankAccountsPage />
          </Lazy>
        ),
      },
      {
        path: 'icerik/hukuki-metinler',
        element: (
          <Lazy>
            <AdminLegalPage />
          </Lazy>
        ),
      },
      {
        path: 'icerik/ayarlar',
        element: (
          <Lazy>
            <AdminSettingsPage />
          </Lazy>
        ),
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
